import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Link2, Layers, Image as ImageIcon, X, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { submitSingleAnalysis, submitBatchAnalysis, isQuotaError } from "@/lib/api";

const MODES = [
  { id: "single", label: "Single image", icon: ImageIcon },
  { id: "batch", label: "Batch mode", icon: Layers },
];

function isLikelyUrl(s) {
  return /^https?:\/\//i.test(s);
}

export default function NewAnalysis() {
  const [mode, setMode] = useState("single");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [batchUrls, setBatchUrls] = useState("");
  const [batchFiles, setBatchFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const navigate = useNavigate();

  const batchItems = useMemo(() => {
    const urls = batchUrls
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .map((u, i) => ({ id: `u_${i}`, fileName: u.split("/").pop() || `url_${i + 1}`, url: u }));
    const files = batchFiles.map((f, i) => ({
      id: `f_${i}_${f.name}`,
      fileName: f.name,
      url: URL.createObjectURL(f),
    }));
    return [...urls, ...files];
  }, [batchUrls, batchFiles]);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const dropped = Array.from(e.dataTransfer.files || []).filter((f) =>
        f.type.startsWith("image/"),
      );
      if (mode === "single") {
        if (dropped[0]) setFile(dropped[0]);
      } else {
        setBatchFiles((prev) => [...prev, ...dropped]);
      }
    },
    [mode],
  );

  const reset = () => {
    setUrl("");
    setFile(null);
    setBatchUrls("");
    setBatchFiles([]);
    setProgress(0);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setProgress(0);
    toast.message("Analysis submitted", { description: "Running reverse image search…" });
    try {
      if (mode === "single") {
        if (!url && !file) {
          toast.error("Add an image URL or drop a file to begin.");
          setSubmitting(false);
          return;
        }

        // Generate a local object URL for uploaded files so downstream screens
        // can show a thumbnail without needing a public URL from the server.
        const localThumbnail = file ? URL.createObjectURL(file) : null;

        const newCase = await submitSingleAnalysis({
          url: url || null,
          file: file || null,
          fileName: file ? file.name : null,
        });

        toast.success("Report ready", { description: newCase.id });

        // Thread the local thumbnail through router state as a fallback.
        navigate(`/source-select/${newCase.id}`, {
          state: { localThumbnail: localThumbnail || newCase.thumbnail },
        });
      } else {
        if (batchItems.length === 0) {
          toast.error("Add at least one URL or file for batch mode.");
          setSubmitting(false);
          return;
        }
        for (let i = 0; i <= batchItems.length; i += 1) {
          setProgress(Math.round((i / batchItems.length) * 100));
          await new Promise((r) => setTimeout(r, 220));
        }
        const { batchId } = await submitBatchAnalysis(batchItems);
        toast.success("Batch ready", { description: `${batchItems.length} reports complete.` });
        navigate(`/batch/${batchId}`);
      }
    } catch (e) {
      if (isQuotaError(e)) {
        // Only Free accounts have a daily limit; Plus/Pro/admin never reach this branch.
        toast.error("Free daily limit reached", {
          description: e.message,
          duration: 8000,
          action: { label: "Go unlimited from £6.99", onClick: () => navigate("/account") },
        });
      } else {
        toast.error("Submission failed", { description: e.message || "Try again in a moment." });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="new-analysis-page">
      <header>
        <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-1">Step 1 of 3</div>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">New analysis</h1>
        <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
          Submit a single image or queue a batch. We'll run a reverse image search, surface
          candidate sources, then return a full metadata report.
        </p>
      </header>

      {/* Mode toggle */}
      <div
        className="inline-flex rounded-lg border border-[#272A30] bg-[#111318] p-1"
        role="tablist"
        aria-label="Analysis mode"
        data-testid="mode-toggle"
      >
        {MODES.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            role="tab"
            aria-selected={mode === id}
            data-testid={`mode-${id}`}
            onClick={() => setMode(id)}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md transition-colors ${
              mode === id ? "bg-white text-[#0A0A0B]" : "text-zinc-400 hover:text-white"
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Inputs */}
      {mode === "single" ? (
        <div className="grid md:grid-cols-2 gap-6" data-testid="single-mode-panel">
          <div className="space-y-3">
            <label className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500">
              Image URL
            </label>
            <div className="relative">
              <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/photo.jpg"
                data-testid="single-url-input"
                className="w-full rounded-lg bg-[#0A0A0B] border border-[#272A30] focus:border-zinc-500 focus:ring-1 focus:ring-zinc-500 outline-none pl-9 pr-3 py-2.5 text-sm placeholder:text-zinc-600"
              />
            </div>
            <p className="text-xs text-zinc-500">
              Or drop a file in the panel on the right. URLs and files can be combined.
            </p>
          </div>

          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            data-testid="single-drop-zone"
            className={`rounded-xl border-2 border-dashed p-8 text-center transition-all cursor-pointer ${
              dragOver
                ? "border-sky-400/60 bg-sky-500/5"
                : "border-[#272A30] bg-[#111318] hover:border-zinc-500 hover:bg-[#14171D]"
            }`}
            onClick={() => document.getElementById("file-input-single").click()}
          >
            <input
              id="file-input-single"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.[0]) setFile(e.target.files[0]);
              }}
            />
            <Upload className="h-7 w-7 mx-auto text-zinc-400" />
            <div className="mt-3 text-sm font-medium">Drop image here</div>
            <div className="text-xs text-zinc-500 mt-1">or click to browse</div>
            {file && (
              <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#272A30] bg-[#0A0A0B] px-3 py-1 text-xs text-zinc-300">
                <ImageIcon className="h-3 w-3" /> {file.name}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                  className="text-zinc-500 hover:text-white"
                  aria-label="Remove file"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6" data-testid="batch-mode-panel">
          <div className="space-y-3">
            <label className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500">
              URLs (one per line)
            </label>
            <textarea
              value={batchUrls}
              onChange={(e) => setBatchUrls(e.target.value)}
              rows={8}
              placeholder={"https://example.com/a.jpg\nhttps://example.com/b.jpg"}
              data-testid="batch-urls-input"
              className="w-full rounded-lg bg-[#0A0A0B] border border-[#272A30] focus:border-zinc-500 focus:ring-1 focus:ring-zinc-500 outline-none px-3 py-2.5 text-sm mono placeholder:text-zinc-600"
            />
          </div>

          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            data-testid="batch-drop-zone"
            className={`rounded-xl border-2 border-dashed p-8 text-center transition-all cursor-pointer ${
              dragOver
                ? "border-sky-400/60 bg-sky-500/5"
                : "border-[#272A30] bg-[#111318] hover:border-zinc-500 hover:bg-[#14171D]"
            }`}
            onClick={() => document.getElementById("file-input-batch").click()}
          >
            <input
              id="file-input-batch"
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                setBatchFiles((prev) => [...prev, ...files]);
              }}
            />
            <Layers className="h-7 w-7 mx-auto text-zinc-400" />
            <div className="mt-3 text-sm font-medium">Drop multiple images here</div>
            <div className="text-xs text-zinc-500 mt-1">or click to browse</div>
          </div>
        </div>
      )}

      {/* Queue */}
      {mode === "batch" && (
        <div
          className="rounded-xl border border-[#272A30] bg-[#111318]"
          data-testid="batch-queue"
        >
          <div className="px-5 py-3 border-b border-[#272A30] flex items-center justify-between">
            <div className="text-sm font-medium">Queue ({batchItems.length})</div>
            {batchItems.length > 0 && (
              <button
                onClick={reset}
                className="text-xs uppercase tracking-wider text-zinc-500 hover:text-white"
                data-testid="batch-clear"
              >
                Clear
              </button>
            )}
          </div>
          {batchItems.length === 0 ? (
            <div className="p-10 text-center text-sm text-zinc-500" data-testid="batch-queue-empty">
              No items yet. Add URLs or drop files to populate the queue.
            </div>
          ) : (
            <ul>
              {batchItems.map((it, i) => (
                <li
                  key={it.id}
                  className="px-5 py-3 border-t border-[#272A30]/60 flex items-center justify-between text-sm"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-[11px] mono text-zinc-500 w-6 text-right">
                      {i + 1}
                    </span>
                    {isLikelyUrl(it.url) ? (
                      <Link2 className="h-3.5 w-3.5 text-zinc-500" />
                    ) : (
                      <ImageIcon className="h-3.5 w-3.5 text-zinc-500" />
                    )}
                    <span className="truncate text-zinc-300">{it.fileName}</span>
                  </div>
                  <span className="text-[11px] uppercase tracking-wider text-zinc-500">
                    {submitting ? "Processing" : "Queued"}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {submitting && (
            <div className="px-5 py-3 border-t border-[#272A30]">
              <div className="flex items-center justify-between text-xs text-zinc-400 mb-2">
                <span>Running analyses…</span>
                <span className="mono tabular-nums">{progress}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-[#1A1D24] overflow-hidden">
                <div
                  className="h-full bg-sky-400 transition-all duration-200"
                  style={{ width: `${progress}%` }}
                  data-testid="batch-progress"
                />
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={reset}
          className="rounded-lg border border-[#272A30] px-4 py-2.5 text-sm text-zinc-300 hover:bg-[#1A1D24] transition-colors"
          data-testid="reset-button"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="submit-analysis-button"
          className="inline-flex items-center gap-2 rounded-lg bg-white text-[#0A0A0B] font-semibold text-sm px-5 py-2.5 hover:bg-zinc-200 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Running…
            </>
          ) : (
            <>Run analysis</>
          )}
        </button>
      </div>
    </div>
  );
}
